import {
  AloftArea,
  AloftPrice,
  SalesComparisonApproachType,
  applyRoundingAndThresholds,
  schAloftPrice,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectOpinionOfSiteValue } from '../opinionOfSiteValueSelectors.ts';
import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectSalesComparisonApproach,
} from '../orderSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectSubjectUnitRateForFeature } from '../propertyDataSelectors/unitRateSelectors.ts';
import { selectSiteSize, selectSiteSizes } from '../selectSiteSize.ts';
import { selectSiteValue, selectSiteValues } from '../selectSiteValue.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { calculateUnitRateAdjustment } from './utils/calculateUnitRateAdjustment.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateSiteAdj = (
  opinionOfSiteValue: ReturnType<typeof selectOpinionOfSiteValue>,
  compSiteValue: ReturnType<typeof selectSiteValue>,
  subjectSiteSize: AloftArea | undefined,
  siteSize: AloftArea | undefined,
  salesComparisonApproach: SalesComparisonApproachType,
  unitRate?: AloftPrice,
) => {
  if (salesComparisonApproach === 'unit_rate') {
    return calculateUnitRateAdjustment({
      subjectUnitAmount: subjectSiteSize?.toSqFt().unitAmount,
      compUnitAmount: siteSize?.toSqFt().unitAmount,
      unitRate,
    });
  }

  if (!opinionOfSiteValue || !compSiteValue) return undefined;
  return schAloftPrice.parse(opinionOfSiteValue).sub(compSiteValue).roundToDollarLike();
};

export const selectSiteAdj = createPriceSelector(
  [
    selectOpinionOfSiteValue,
    selectSiteValue,
    (state) => selectSiteSize(state, 'subject'),
    selectSiteSize,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'siteSize'),
  ],
  calculateSiteAdj,
);

export const selectSiteAdjs = createSelector(
  [
    selectOpinionOfSiteValue,
    selectSiteValues,
    selectCompSequence,
    selectSiteSizes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'siteSize'),
  ],
  (opinionOfSiteValue, siteValues, compSequence, siteSizes, salesComparisonApproach, unitRate) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateSiteAdj(
        opinionOfSiteValue,
        siteValues[propID],
        siteSizes.subject,
        siteSizes[propID],
        salesComparisonApproach,
        unitRate,
      );

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);

export const selectSiteAdjsDisplay = createSelector([selectSiteAdjs], (siteAdjs) =>
  Object.fromEntries(Object.entries(siteAdjs).map(([id, adj]) => [id, adj.toString(true)])),
);

const selectAppliedSiteAdj = createPriceSelector(
  [selectSiteAdj, selectAdjustmentSettings],
  (adj, adjSettings) => applyRoundingAndThresholds(adj, 'site', adjSettings),
);

export const selectAppliedSiteAdjDisplay = createSelector([selectAppliedSiteAdj], (adj) => {
  if (!adj) return undefined;

  return {
    value: getStringValueFromAdjustment(adj),
    color: getColorFromAdjustment(adj),
  };
});

export const selectAppliedSiteAdjsDisplay = createSelector(
  [selectSiteAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (siteAdjs, adjSettings, manualAdjustmentsEntities) =>
    Object.keys(siteAdjs).reduce((acc: Record<string, { value: string; color: string }>, id) => {
      const adj = siteAdjs[id];
      const manualAdjustment = manualAdjustmentsEntities[id]?.site;

      if (manualAdjustment) {
        const manualAdjustmentPrice = schAloftPrice.parse(manualAdjustment);
        acc[id] = {
          value: `${getStringValueFromAdjustment(manualAdjustmentPrice)}*`,
          color: getColorFromAdjustment(manualAdjustmentPrice),
        };
      } else if (adj) {
        acc[id] = {
          value: getStringValueFromAdjustment(applyRoundingAndThresholds(adj, 'site', adjSettings)),
          color: getColorFromAdjustment(adj),
        };
      }

      return acc;
    }, {}),
);
